/** TradePlanComparison — plan grid + selected-plan detail + critic on plan. */
import { CheckCircle2, ChevronsRight, Wand2 } from "lucide-react";
import { PROP } from "@/constants/testIds";
import SuggestedModsDiff from "./SuggestedModsDiff";

const VERDICT_STYLE = {
  approve: { c: "pnl-up border-current", label: "APPROVE" },
  modify: { c: "text-signal-yellow border-current", label: "MODIFY" },
  reject: { c: "pnl-down border-current", label: "REJECT" },
};

function SuggestedMods({ mods, currentEntry, currentStop, currentTarget, currentQty, onApplyField, onApplyAll, proposalId, planId, portfolioKey }) {
  const asNum = (v) => (v === "" || v == null ? null : Number(v));
  const norm = {
    entry: mods?.entry,
    stop: mods?.stop ?? (Array.isArray(mods?.stops) ? mods.stops[0] : undefined),
    target: mods?.target ?? (Array.isArray(mods?.targets) ? mods.targets[0] : undefined),
    qty: mods?.qty ?? mods?.quantity,
  };
  const cur = {
    entry: asNum(currentEntry), stop: asNum(currentStop),
    target: asNum(currentTarget), qty: asNum(currentQty),
  };
  const rows = [
    { key: "entry",  label: "Entry",  suggested: norm.entry,  current: cur.entry },
    { key: "stop",   label: "Stop",   suggested: norm.stop,   current: cur.stop },
    { key: "target", label: "Target", suggested: norm.target, current: cur.target },
    { key: "qty",    label: "Qty",    suggested: norm.qty,    current: cur.qty },
  ].filter((r) => r.suggested !== undefined && r.suggested !== null && Number(r.suggested) !== r.current);
  const extraTargets = Array.isArray(mods?.targets) && mods.targets.length > 1 ? mods.targets.slice(1) : [];

  if (rows.length === 0) {
    return (
      <div className="border border-border/50 p-3 text-xs" data-testid={PROP.suggestedModsCard}>
        <div className="flex items-center justify-between">
          <div className="label-cap">Suggested modifications</div>
          <span className="chip pnl-up border-current text-[10px]">Already aligned</span>
        </div>
        <div className="text-muted-foreground mt-1">Your current values already match the critic&apos;s suggestion — no change needed.</div>
      </div>
    );
  }

  const applyAllPayload = Object.fromEntries(rows.map((r) => [r.key, r.suggested]));
  const delta = (from, to) => {
    if (from == null || !isFinite(from)) return null;
    const d = to - from;
    return d === 0 ? null : { sign: d > 0 ? "+" : "", pct: from ? (d / from * 100) : null, abs: d };
  };
  const fmt = (v) => (v == null ? "—" : (Math.abs(v) >= 100 ? Number(v).toFixed(2) : Number(v).toFixed(3).replace(/\.?0+$/, "")));

  return (
    <div className="border border-border/50 p-3 text-xs" data-testid={PROP.suggestedModsCard}>
      <div className="flex items-center justify-between mb-2">
        <div className="label-cap flex items-center gap-2">
          <Wand2 size={12} className="text-signal-yellow" aria-hidden="true"/> Suggested modifications
        </div>
        <button
          onClick={() => onApplyAll(applyAllPayload)}
          className="chip pnl-up border-current text-[10px] hover:opacity-80 transition-opacity focus:outline-none focus:ring-1 focus:ring-current"
          data-testid={PROP.applyAllSuggestions}
        >
          <CheckCircle2 size={11} className="inline mr-1"/>Apply all
        </button>
      </div>
      <div className="space-y-1">
        {rows.map((r) => {
          const d = delta(r.current, Number(r.suggested));
          return (
            <div key={r.key} className="flex items-center gap-2 border border-border/40 px-2 py-1.5 bg-secondary/20 flex-wrap">
              <div className="label-cap w-14">{r.label}</div>
              <div className="mono text-[11px] text-muted-foreground line-through decoration-1">{fmt(r.current)}</div>
              <ChevronsRight size={12} className="text-muted-foreground shrink-0" aria-hidden="true"/>
              <div className="mono text-[11px]">{fmt(Number(r.suggested))}</div>
              {d && (
                <span className={`mono text-[10px] ${d.abs > 0 ? "pnl-up" : "pnl-down"}`}>
                  {d.sign}{fmt(d.abs)}{d.pct != null ? ` (${d.sign}${d.pct.toFixed(1)}%)` : ""}
                </span>
              )}
              <button
                onClick={() => onApplyField(r.key, r.suggested)}
                className="ml-auto text-[10px] mono uppercase tracking-wide border border-border/60 hover:bg-secondary px-2 py-0.5 transition-colors focus:outline-none focus:ring-1 focus:ring-current"
                data-testid={`${PROP.applySuggestion}-${r.key}`}
              >Apply</button>
            </div>
          );
        })}
      </div>
      {extraTargets.length > 0 && (
        <div className="mt-2 text-[10px] text-muted-foreground">
          Additional targets suggested: {extraTargets.map(fmt).join(" · ")}
          <span className="ml-1">(secondary targets are informational — the primary drives the form)</span>
        </div>
      )}
      {proposalId && planId && (
        <div className="mt-3">
          <SuggestedModsDiff
            proposalId={proposalId}
            planId={planId}
            portfolioKey={portfolioKey}
            currentValues={{
              entry: Number(currentEntry) || undefined,
              stop:  Number(currentStop)  || undefined,
              target:Number(currentTarget)|| undefined,
              qty:   Number(currentQty)   || undefined,
            }}
            draftValues={applyAllPayload}
            onApply={() => onApplyAll(applyAllPayload)}
          />
        </div>
      )}
    </div>
  );
}

export default function TradePlanComparison({
  plans, reviewMap, selectedPlanId, selectedPlan, recommendedId, criticConcerns,
  currentEntry, currentStop, currentTarget, currentQty, critic,
  onSelectPlan, onApplyField, onApplyAll,
  proposalId, portfolioKey,
}) {
  return (
    <section className="bg-card border border-border/60" data-testid={PROP.plansTable}>
      <div className="flex items-center gap-3 px-4 h-10 border-b border-border/60">
        <div className="label-cap">AI-Generated Trade Plans</div>
        <div className="text-[10px] mono text-muted-foreground uppercase tracking-widest">
          Critic-recommended = {recommendedId || "—"} · Click a plan to select
        </div>
      </div>
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-background/20 border-b border-border/40">
            <tr>
              <th className="text-left px-3 py-2 font-normal">Plan</th>
              <th className="text-left px-3 py-2 font-normal">Style</th>
              <th className="text-right px-3 py-2 font-normal">Entry</th>
              <th className="text-right px-3 py-2 font-normal">Stop</th>
              <th className="text-right px-3 py-2 font-normal">Target</th>
              <th className="text-right px-3 py-2 font-normal">R/R</th>
              <th className="text-right px-3 py-2 font-normal">A Conf</th>
              <th className="text-right px-3 py-2 font-normal">Critic Score</th>
              <th className="text-left px-3 py-2 font-normal">Critic Verdict</th>
              <th className="text-left px-3 py-2 font-normal">Holding</th>
              <th className="text-right px-3 py-2 font-normal" aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => {
              const r = reviewMap[p.id] || {};
              const isSel = selectedPlanId === p.id;
              const isRec = recommendedId === p.id;
              const v = VERDICT_STYLE[r.verdict] || { c: "text-muted-foreground border-current", label: "—" };
              return (
                <tr key={p.id}
                  onClick={() => onSelectPlan(p.id)}
                  className={`border-b border-border/30 cursor-pointer hover:bg-secondary/25 ${isSel ? "bg-secondary/35" : ""}`}
                  data-testid={PROP.planRow(p.id)}
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-1 h-6 ${isSel ? "bg-[#0A84FF]" : "bg-transparent"}`} aria-hidden="true"/>
                      <div className="mono uppercase">{p.id}</div>
                      {isRec && <span className="chip !text-[9px] pnl-up border-current">CRITIC PICK</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 mono uppercase text-xs text-muted-foreground">{p.style}</td>
                  <td className="px-3 py-2 mono text-right">${p.entry?.toFixed(2)}</td>
                  <td className="px-3 py-2 mono text-right pnl-down">${p.stop?.toFixed(2)}</td>
                  <td className="px-3 py-2 mono text-right pnl-up">${p.targets?.[0]?.price?.toFixed(2)}</td>
                  <td className="px-3 py-2 mono text-right">{Number(p.reward_risk).toFixed(2)}:1</td>
                  <td className="px-3 py-2 mono text-right">{p.confidence}%</td>
                  <td className="px-3 py-2 mono text-right">{r.critic_score ?? "—"}</td>
                  <td className="px-3 py-2"><span className={`chip ${v.c}`}>{v.label}</span></td>
                  <td className="px-3 py-2 mono uppercase text-xs">{p.holding_period}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); onSelectPlan(p.id); }}
                      className={`btn-outline h-7 !px-2 text-[11px] ${isSel ? "!bg-secondary/70" : ""}`}
                      data-testid={PROP.selectPlan(p.id)}
                    >{isSel ? <><CheckCircle2 size={12}/> Selected</> : <>Select</>}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {selectedPlan && (
        <div className="border-t border-border/60 p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="label-cap">Plan {selectedPlan.id} · {selectedPlan.label} — Reasoning</div>
            <p className="text-sm">{selectedPlan.reasoning}</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="border border-border/50 p-2">
                <div className="label-cap">Invalidation</div>
                <div className="mt-1">{selectedPlan.invalidation}</div>
              </div>
              <div className="border border-border/50 p-2">
                <div className="label-cap">Partial Take</div>
                <div className="mt-1">{selectedPlan.partial_take_recommendation || "—"}</div>
              </div>
              <div className="border border-border/50 p-2">
                <div className="label-cap">Trailing Stop</div>
                <div className="mt-1">{selectedPlan.trailing_stop_recommendation || "—"}</div>
              </div>
              <div className="border border-border/50 p-2">
                <div className="label-cap">Holding Period</div>
                <div className="mt-1 mono uppercase">{selectedPlan.holding_period}</div>
              </div>
            </div>
          </div>

          <div className="space-y-2" data-testid={PROP.criticCard}>
            <div className="label-cap flex items-center gap-2">
              <div className="w-2 h-2 bg-signal-red" aria-hidden="true"/>Risk Critic on Plan {selectedPlan.id}
            </div>
            {reviewMap[selectedPlan.id] ? (
              <>
                <div className="flex items-center gap-2 text-xs">
                  <span className={`chip ${VERDICT_STYLE[reviewMap[selectedPlan.id].verdict]?.c}`}>
                    {VERDICT_STYLE[reviewMap[selectedPlan.id].verdict]?.label}
                  </span>
                  <span className="text-muted-foreground">score {reviewMap[selectedPlan.id].critic_score}/100</span>
                </div>
                <p className="text-sm">{reviewMap[selectedPlan.id].notes}</p>
                {reviewMap[selectedPlan.id].modifications && (
                  <SuggestedMods
                    mods={reviewMap[selectedPlan.id].modifications}
                    currentEntry={currentEntry}
                    currentStop={currentStop}
                    currentTarget={currentTarget}
                    currentQty={currentQty}
                    onApplyField={onApplyField}
                    onApplyAll={onApplyAll}
                    proposalId={proposalId}
                    planId={selectedPlan.id}
                    portfolioKey={portfolioKey}
                  />
                )}
              </>
            ) : <div className="text-xs text-muted-foreground">No review returned for this plan.</div>}
            {criticConcerns?.length ? (
              <div>
                <div className="label-cap mb-1 mt-2">Portfolio-level concerns</div>
                <ul className="text-xs list-disc list-inside space-y-0.5 text-muted-foreground">{criticConcerns.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}
